import "@app/App.css";
import { useIsMobile } from "@comps/hooks/use-mobile";
import { ColorPicker } from "@comps/ui/ColorPicker";
import { IconPicker } from "@comps/ui/IconPicker";
import { Button } from "@comps/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { Label } from "@comps/ui/label";
import { Switch } from "@comps/ui/switch";
import { Textarea } from "@comps/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { LucideIconType, SaxIconType } from "@dto/CompozzIcon";
import { Workspace } from "@dto/workspaces";
import { zodResolver } from "@hookform/resolvers/zod";
import { GRAY_300_COLOR, WorkspaceColorType, getRandomColor } from "@utils/colors";
import { DEFAULT_ORDER_INCREMENT } from "@utils/constants";
import { getRandomIcon } from "@utils/icons";
import { TW_Button, TW_ErrorMessage, getWsButtonStyle, useHover } from "@utils/twClasses";
import { MessageQuestion } from "iconsax-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { translate, translateHtml } from "src/i18n/i18n";
import { useModelStore } from "src/store";
import { z } from "zod";

export type CallBackEnd<T> = (body: any) => Promise<T | null | CompozzError>;

interface WorkspaceFormProps {
  initialWorkspace: Workspace | null;
  callBackEnd: CallBackEnd<Workspace>;
  onValidate: (workspace: Workspace) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export default function WorkspaceForm({ initialWorkspace, callBackEnd, onValidate, onCancel }: WorkspaceFormProps) {
  const { t } = useTranslation();
  const workspaces = useModelStore(state => state.workspaces);
  const [error, setError] = useState<string | null>(null);
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelButtonStyle = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okButtonStyle = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const isMobile = useIsMobile();
  const formSchema = z.object({
    workspaceName: z.string().min(1, {
      message: t("workspace.dialog.error.name.missing"),
    }),
    workspaceDescription: z.string().optional(),
    workspaceColor: z.string(),
    workspaceIcon: z.object({
      name: z.string(),
      type: z.enum([SaxIconType, LucideIconType]),
    }),
    workspaceShared: z.boolean().optional(),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workspaceName: initialWorkspace?.name ?? t("workspace.dialog.name.default.value"),
      workspaceDescription: initialWorkspace?.description ?? undefined,
      workspaceColor: initialWorkspace?.color ?? getRandomColor(WorkspaceColorType),
      workspaceIcon: initialWorkspace?.icon ?? getRandomIcon(),
      workspaceShared: initialWorkspace?.shared ?? false,
    },
  })

  function cancel(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    onCancel(event);
  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    let order = initialWorkspace?.order ?? 0;
    if (!initialWorkspace) {
      if (workspaces.length > 0) {
        const lastWS = workspaces[workspaces.length - 1];
        order = (lastWS.order ?? 0) + DEFAULT_ORDER_INCREMENT;
      }
    }
    const newWorkspace: Workspace = {
      _key: initialWorkspace?._key ?? "",
      name: values.workspaceName,
      order: order,
      description: values.workspaceDescription ?? null,
      color: values.workspaceColor,
      icon: values.workspaceIcon,
      shared: values.workspaceShared ?? false,
      drive: initialWorkspace?.drive ?? false,
    }
    callBackEnd(newWorkspace).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        } else {
          setError(null);
          onValidate(response as Workspace);
        }
      } else {
        setError(null);
        onValidate(newWorkspace as Workspace);
      }
    })
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      form.handleSubmit(onSubmit)(event);
    }
  }

  return (
    <Form {...form}>
      <div className={'max-h-[80vh] overflow-y-auto scrollbar-thin scrollbar-thumb-compozz-purple scrollbar-track-compozz-purple-light scrollbar-button-hidden'}>
        <form onSubmit={form.handleSubmit(onSubmit)} className={`space-y-4 ${isMobile ? 'px-2' : ''}`}>
        <FormField
          control={form.control}
          name="workspaceName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("workspace.dialog.name.label")}</FormLabel>
              <FormControl>
                <Input placeholder={t("workspace.dialog.name.placeholder")} {...field} onKeyDown={handleKeyDown} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="workspaceDescription"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("workspace.dialog.description.label")}</FormLabel>
              <FormControl>
                <Textarea placeholder={t("workspace.dialog.description.placeholder")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className={`flex flex-row justify-evenly items-center gap-4`}>
          <FormField
            control={form.control}
            name="workspaceColor"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("workspace.dialog.color.label")}</FormLabel>
                <FormControl>
                  <ColorPicker color={field.value} onChange={(color) => field.onChange(color)} colorType={WorkspaceColorType} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="workspaceIcon"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("workspace.dialog.icon.label")}</FormLabel>
                <FormControl>
                  <IconPicker icon={field.value} color={form.getValues("workspaceColor")} onChange={(icon) => field.onChange(icon)} side="right" align="center" sideOffset={10} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col justify-start items-start gap-4">
          <Label className="mt-2">{t("workspace.dialog.shared.title")}</Label>
          <p className="text-sm text-gray-500">
            {translateHtml(t, "workspace.dialog.shared.description", [], "", false)}
            <TooltipProvider>
              <Tooltip delayDuration={100}>
                <TooltipTrigger className="inline-block">
                  <MessageQuestion className="ml-2 h-6 w-6 min-h-6 min-w-6 text-gray-600 inline-block" />
                </TooltipTrigger>
                <TooltipContent>
                  {translateHtml(t, "workspace.dialog.shared.description.tooltip", [])}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </p>
          <div className="flex flex-row justify-start items-center gap-4">
            <Label>{t("workspace.dialog.shared.label")}</Label>
            <FormField
              control={form.control}
              name="workspaceShared"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Switch className="mt-1" checked={field.value} onCheckedChange={field.onChange} style={{ backgroundColor: (field.value ? wsColor : GRAY_300_COLOR) }} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        {error && <div className={TW_ErrorMessage}>{error}</div>}
        {!error && <div className={`${TW_ErrorMessage} ${isMobile ? 'text-sm break-words' : ''}`}></div>}
        <div className="ButtonList">
          <Button className={`${TW_Button} ${isMobile ? 'w-full' : ''}`} {...cancelButtonStyle} onClick={cancel}>{t("general.button.cancel.text")}</Button>
          <Button className={`${TW_Button} ${isMobile ? 'w-full' : ''}`} {...okButtonStyle} type="submit">{t("general.button.confirm.text")}</Button>
        </div>
        </form>
      </div>
    </Form>
  )
}